import { cubaDayKey, cubaWeekStartDayKey } from '../time/cubaTime';
import {
  getActiveWindow,
  resolveActiveTurnDayKey,
  resolveWindowSchedule,
  type WindowScheduleConfig,
} from './windowSchedule';

export type PlayWindow = 'day' | 'night';

export interface PlayTurn {
  dayKey: string;
  window: PlayWindow;
}

export interface TurnPlayLike {
  id?: string;
  dayKey?: string;
  window?: string;
  createdAt?: number | { toMillis?: () => number; seconds?: number };
  status?: string;
}

export interface HistoryTurnGroup {
  turn: PlayTurn;
  labelKey: 'playsTurnDay' | 'playsTurnNight';
  dayKey: string;
  plays: TurnPlayLike[];
}

export interface PartitionedPlays {
  currentTurn: PlayTurn | null;
  /** Today's day/night groups (newest turn first). */
  todayGroups: HistoryTurnGroup[];
  /** Earlier days in the current Cuba week (Mon–Sun), excluding today. */
  historyGroups: HistoryTurnGroup[];
  /** Before this week's Monday. */
  archivedGroups: HistoryTurnGroup[];
}

/** Current open acceptance turn, or null when between windows. */
export function getCurrentTurn(
  config: WindowScheduleConfig | null | undefined,
  nowUtc: Date = new Date(),
): PlayTurn | null {
  const window = getActiveWindow(config, nowUtc);
  if (!window) return null;
  return {
    dayKey: resolveActiveTurnDayKey(config, window, nowUtc),
    window,
  };
}

function resolveCreatedAtMs(raw: TurnPlayLike['createdAt']): number | null {
  if (raw == null) return null;
  if (typeof raw === 'number') return raw;
  if (typeof raw?.toMillis === 'function') return raw.toMillis();
  if (typeof raw?.seconds === 'number') return raw.seconds * 1000;
  return null;
}

/** Infer turn identity from stamped fields, falling back to createdAt day. */
export function playTurnOf(play: TurnPlayLike, nowUtc: Date = new Date()): PlayTurn | null {
  const window = play.window === 'night' ? 'night' : play.window === 'day' ? 'day' : null;
  if (!window) return null;
  const dayKey = play.dayKey
    || (() => {
      const ms = resolveCreatedAtMs(play.createdAt);
      return ms != null ? cubaDayKey(new Date(ms)) : cubaDayKey(nowUtc);
    })();
  return { dayKey, window };
}

function turnKey(turn: PlayTurn): string {
  return `${turn.dayKey}:${turn.window}`;
}

function compareTurnsNewestFirst(a: PlayTurn, b: PlayTurn): number {
  if (a.dayKey !== b.dayKey) return a.dayKey < b.dayKey ? 1 : -1;
  if (a.window === b.window) return 0;
  return a.window === 'night' ? -1 : 1;
}

function toGroups(
  map: Map<string, { turn: PlayTurn; plays: TurnPlayLike[] }>,
): HistoryTurnGroup[] {
  return [...map.values()]
    .sort((a, b) => compareTurnsNewestFirst(a.turn, b.turn))
    .map(({ turn, plays }) => ({
      turn,
      dayKey: turn.dayKey,
      labelKey: turn.window === 'night' ? 'playsTurnNight' as const : 'playsTurnDay' as const,
      plays,
    }));
}

function pushToMap(
  map: Map<string, { turn: PlayTurn; plays: TurnPlayLike[] }>,
  turn: PlayTurn,
  play: TurnPlayLike,
) {
  const key = turnKey(turn);
  const group = map.get(key);
  if (group) group.plays.push(play);
  else map.set(key, { turn, plays: [play] });
}

/**
 * Split plays into:
 * - todayGroups: each turn of the Cuba calendar day
 * - historyGroups: other days in the current week (Mon–Sun)
 * - archivedGroups: before this week's Monday
 */
export function partitionPlaysByTurn(
  plays: TurnPlayLike[],
  config: WindowScheduleConfig | null | undefined,
  nowUtc: Date = new Date(),
): PartitionedPlays {
  resolveWindowSchedule(config);

  const todayKey = cubaDayKey(nowUtc);
  const weekStartKey = cubaWeekStartDayKey(nowUtc);
  const currentTurn = getCurrentTurn(config, nowUtc);
  /** Calendar today + overnight open-day still in progress after midnight. */
  const todayKeys = new Set<string>([todayKey]);
  if (currentTurn?.dayKey) todayKeys.add(currentTurn.dayKey);

  const todayMap = new Map<string, { turn: PlayTurn; plays: TurnPlayLike[] }>();
  const historyMap = new Map<string, { turn: PlayTurn; plays: TurnPlayLike[] }>();
  const archivedMap = new Map<string, { turn: PlayTurn; plays: TurnPlayLike[] }>();

  for (const play of plays) {
    const turn = playTurnOf(play, nowUtc);
    if (!turn) continue;
    if (todayKeys.has(turn.dayKey)) {
      pushToMap(todayMap, turn, play);
    } else if (turn.dayKey >= weekStartKey) {
      pushToMap(historyMap, turn, play);
    } else {
      pushToMap(archivedMap, turn, play);
    }
  }

  return {
    currentTurn,
    todayGroups: toGroups(todayMap),
    historyGroups: toGroups(historyMap),
    archivedGroups: toGroups(archivedMap),
  };
}

/** Flat list of plays belonging to Cuba “today” turns (same scope as Jugadas → Hoy). */
export function playsInTodayTurns<T extends TurnPlayLike>(
  plays: T[],
  config: WindowScheduleConfig | null | undefined,
  nowUtc: Date = new Date(),
): T[] {
  return partitionPlaysByTurn(plays, config, nowUtc)
    .todayGroups
    .flatMap((group) => group.plays as T[]);
}
